import { useRouter } from "next/router";
import { FormEvent, useState } from "react";
import { useEffect } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";

import Loading from "../../components/Loading";
import PlaylistList from "../../components/playlist/playlist-list";

import useDebounce from "../../hooks/use-debounce";
import usePaginatedPlaylists from "../../hooks/playlists/use-paginated-playlists";
import { CategoryType, EventType } from "../../context/analytics-context";
import usePageEvents from "../../hooks/use-page-events";
import HeadMeta from "../../components/HeadMeta";

export default function Playlists() {
  const router = useRouter();
  const _term = (router.query?.q as string) || "";

  const [searchTerm, setSearchTerm] = useState(_term);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 3000);

  const sendAnalytics = usePageEvents("playlists");

  const {
    data: playlists,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isError,
    isFetchingNextPage
  } = usePaginatedPlaylists(searchQuery);

  const [infiniteRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    disabled: isError,
    rootMargin: "0px 0px 400px 0px",
    onLoadMore: () => !isFetchingNextPage && fetchNextPage()
  });

  const goBack = () => {
    router.back();
  };

  useEffect(() => {
    setSearchQuery(debouncedSearchTerm);

    if (debouncedSearchTerm?.length > 0) {
      sendAnalytics({
        category: CategoryType.APP,
        type: EventType.SEARCH,
        page: "playlists",
        query: debouncedSearchTerm
      });
    }
  }, [debouncedSearchTerm]);

  return (
    <div className="w-full overflow-y-auto">
      <HeadMeta title="Playlists" url="/playlists" />

      <div className="p-3">
        <div className="flex items-center">
          <button
            onClick={goBack}
            className="flex items-center space-x-2 focus:outline-none focus:text-gray-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-8 h-8 -ml-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <div className="flex items-center pb-1 ml-5 sm:ml-6">
            <p className="flex-1 text-2xl font-bold text-gray-800 sm:text-3xl">Playlists</p>
          </div>
        </div>

        <div className="mt-5">
          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              setSearchQuery(searchTerm);
            }}
          >
            <input
              type="text"
              value={searchTerm}
              placeholder="Search Playlists"
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                setSearchTerm(e.currentTarget.value)
              }
              className="w-full px-6 py-3 text-white search-input"
            />
          </form>
        </div>

        <div className="flex flex-col justify-center flex-1 mt-3">
          <PlaylistList playlists={playlists} isLoading={false} />

          {(isLoading || hasNextPage) && (
            <div className="" ref={infiniteRef}>
              <Loading />
            </div>
          )}

          {isError && (
            <div className="">
              <div className="flex flex-col items-center justify-center h-full p-4 text-sm">
                <p>An error occured. Try again later.</p>
                <button
                  onClick={() => setSearchTerm("")}
                  className="px-4 py-2.5 text-white rounded-md mt-2 bg-orange active:bg-yellow-600"
                >
                  Clear Search
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
